import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { FileListRecord } from 'src/app/modules/shared/models/api.model';
import { DocumentFoldersService, DocumentNavigationCategory, DocumentNavigationType } from 'src/app/modules/shared/services/document-folders/document-folders.service';
import { DocumentService } from 'src/app/modules/shared/services/document/document.service';
import { ToasterService } from 'src/app/modules/shared/services/toaster/toaster.service';
import { PendingReviewsService } from 'src/app/modules/shared/services/pending-reviews/pending-reviews.service';
import { UserRoleService } from 'src/app/modules/shared/services/user-role/user-role.service';

@Component({
  selector: 'app-folders-left-nav',
  templateUrl: './folders-left-nav.component.html',
  styleUrls: ['./folders-left-nav.component.scss']
})
export class FoldersLeftNavComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];

  isLoading = false;
  loadingTypes = false;
  loadingFiles = false;

  // Pending reviews
  pending_review_count = 0;
  is_manager_or_higher = false;

  categories: DocumentNavigationCategory[] = [];
  types: DocumentNavigationType[] = [];
  files: FileListRecord[] = [];

  selectedTypeId: number | null = null;
  selectedCategoryId: number | null = null;
  selectedFileId: string | null = null;
  isSidebarOpen: boolean = false;

  totalFilesForType = 0;
  currentPageForType = 1;
  totalPages = 0;

  pageSize = 10;

  constructor(
    private documentFoldersService: DocumentFoldersService,
    private documentService: DocumentService,
    private Toaster: ToasterService,
    private pendingReviewsService: PendingReviewsService,
    private userRoleService: UserRoleService
  ) { }

  private async getCategories() {
    try {
      this.isLoading = true;
      const categories = await this.documentFoldersService.getCategories();
      this.categories = categories.map(category => {
        return {
          category_name: category.category_name.replace(/_/g, ' '),
          document_count: category.document_count,
          category_id: category.category_id
        }
      });

      // Auto-select first category if available (left nav pattern)
      if (this.categories.length > 0 && !this.selectedCategoryId) {
        this.selectCategory(this.categories[0].category_id);
      }
    } catch (error) {
      this.Toaster.warning(`Error fetching folders: ${error}`, 4000);
    } finally {
      this.isLoading = false;
    }
  }

  public async getTypesForCategory(category_id: number) {
    try {
      this.loadingTypes = true;
      this.types = await this.documentFoldersService.getTypes(category_id);
    } catch (error) {
      this.Toaster.warning(`Error fetching types for category ${category_id}: ${error}`, 4000);
    } finally {
      this.loadingTypes = false;
    }
  }

  public async getFilesForType(type_id: number, page = 1) {
    try {
      this.loadingFiles = true;

      const res = await this.documentService.listFiles({
        fileType: [type_id],
        page,
        pageSize: this.pageSize,
        search: '',
      });

      this.files = res.files || [];
      this.totalFilesForType = res.totalItems ?? 0;
      this.totalPages = Math.ceil(this.totalFilesForType / this.pageSize) || 0;
      this.currentPageForType = page;

    } catch (error) {
      this.Toaster.warning(`Error fetching files for type ${type_id}: ${error}`, 4000);
    } finally {
      this.loadingFiles = false;
    }
  }

  public get showingFrom(): number {
    if (this.totalFilesForType === 0) return 0;
    return (this.currentPageForType - 1) * this.pageSize + 1;
  }

  public get showingTo(): number {
    return Math.min(this.currentPageForType * this.pageSize, this.totalFilesForType);
  }

  public goToPage(page: number): void {
    if (page < 1) return;
    if (this.totalPages && page > this.totalPages) return;
    this.currentPageForType = page;
    this.getFilesForType(this.selectedTypeId ?? 0, page);
  }

  public nextPage(): void {
    if (this.currentPageForType < this.totalPages) {
      this.goToPage(this.currentPageForType + 1);
    }
  }

  public prevPage(): void {
    if (this.currentPageForType > 1) {
      this.goToPage(this.currentPageForType - 1);
    }
  }

  public firstPage(): void {
    this.goToPage(1);
  }

  public lastPage(): void {
    if (this.totalPages) {
      this.goToPage(this.totalPages);
    }
  }

  public onPageSizeChange(size: number): void {
    if (!size || size < 1) return;
    this.pageSize = size;
    this.currentPageForType = 1;
    this.getFilesForType(this.selectedTypeId ?? 0, 1);
  }

  // Left nav pattern: select category (always keeps one selected)
  public selectCategory(category_id: number) {
    this.selectedCategoryId = category_id;
    this.selectedTypeId = null;
    this.files = [];
    this.totalFilesForType = 0;
    this.currentPageForType = 1;
    this.closeSidebar();
    this.getTypesForCategory(category_id);
  }

  // Toggle type to show files
  public toggleType(type_id: number, document_count: number) {
    if (document_count === 0) {
      this.Toaster.warning('This document type has no files', 3000);
      return;
    }

    if (this.selectedTypeId === type_id) {
      this.selectedTypeId = null;
      this.files = [];
      this.totalFilesForType = 0;
      this.currentPageForType = 1;
      this.closeSidebar();
    } else {
      this.totalFilesForType = 0;
      this.currentPageForType = 1;
      this.selectedTypeId = type_id;
      this.getFilesForType(type_id);
      this.closeSidebar();
    }
  }

  public selectFile(fileId: string): void {
    this.selectedFileId = fileId;
    this.isSidebarOpen = true;
  }

  public closeSidebar(): void {
    this.isSidebarOpen = false;
  }

  // Get current category name for display
  public get currentCategoryName(): string {
    const category = this.categories.find(c => c.category_id === this.selectedCategoryId);
    return category?.category_name || 'Select a folder';
  }

  ngOnInit(): void {
    this.getCategories();

    this.is_manager_or_higher = this.userRoleService.isManagerOrHigher();

    this.subscriptions.push(
      this.pendingReviewsService.pending_review_count$.subscribe(count => {
        this.pending_review_count = count;
      })
    );

    this.subscriptions.push(
      this.userRoleService.userRole$.subscribe(() => {
        this.is_manager_or_higher = this.userRoleService.isManagerOrHigher();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
